import React, { useEffect, useMemo, useState } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { Line, Bar, Doughnut } from "react-chartjs-2";
import api from "../../services/api";
import { formatDate } from "../../lib/dateFormat";
import DateInput from "../../components/DateInput";
import AttendanceReportPanel from "../../components/reports/AttendanceReportPanel";
import toast from "react-hot-toast";
import AdminPageHeader from "../../components/admin/AdminPageHeader";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

type StatBlock = { value: number; value_eur_cents?: number; change_pct: number | null };

interface ReportsOverview {
  period: { start: string; end: string };
  stats: {
    total_users: StatBlock;
    active_clubs: StatBlock;
    sessions_this_month: StatBlock;
    quiz_completions: StatBlock;
    total_points_awarded: StatBlock;
    revenue_ron_cents: StatBlock;
  };
  monthly_growth: Array<{ month: string; new_users: number; active_users: number }>;
  weekday_activity: Array<{ weekday: number; sessions: number; attendance: number }>;
  revenue_by_status: Array<{ status: string; ron_cents: number; eur_cents: number }>;
  points_by_source: Array<{ source: string; points: number }>;
  top_clubs: Array<{ id: string; name: string; athletes: number; sessions: number; revenue_ron_cents: number }>;
  top_athletes: Array<{ id: string; name: string; points: number; quizzes: number; attendance_rate: number }>;
}

const MONTH_LABELS_RO = [
  "ian", "feb", "mar", "apr", "mai", "iun",
  "iul", "aug", "sep", "oct", "noi", "dec",
];

const WEEKDAY_LABELS_RO = ["Dum", "Lun", "Mar", "Mie", "Joi", "Vin", "Sâm"];

const STATUS_LABEL: Record<string, string> = {
  draft: "Draft",
  open: "Emise",
  paid: "Plătite",
  void: "Anulate",
  uncollectible: "Neîncasabile",
  cancelled: "Anulate",
};

const SOURCE_LABEL: Record<string, string> = {
  attendance: "Prezență",
  quiz: "Quiz-uri",
  video: "Video-uri",
  event: "Evenimente",
  competition: "Competiții",
  bonus_vulnerable: "Bonus vulnerabili",
  admin_adjustment: "Ajustare admin",
};

export default function ReportsAnalytics() {
  // Two different things are called "rapoarte": the platform's own numbers,
  // and the attendance register a club or a federation asks for. Both live
  // here, because this is the URL somebody looking for either one opens.
  const [tab, setTab] = useState<"analytics" | "attendance">("analytics");
  const todayIso = new Date().toISOString().split("T")[0];
  const thirtyAgoIso = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split("T")[0];

  const [dateRange, setDateRange] = useState({ start: thirtyAgoIso, end: todayIso });
  const [data, setData] = useState<ReportsOverview | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchData = async (range: { start: string; end: string }) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (range.start) params.append("start", range.start);
      if (range.end) params.append("end", range.end);
      const { data: payload } = await api.get<ReportsOverview>(
        "/reports/overview?" + params.toString()
      );
      setData(payload);
    } catch (err: any) {
      console.error("Failed to load reports overview:", err);
      toast.error(err?.response?.data?.error || "Nu s-au putut încărca datele de raport");
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(dateRange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onApplyRange = () => fetchData(dateRange);

  const stats = useMemo(() => {
    if (!data) return [];
    return [
      { label: "Total utilizatori", value: data.stats.total_users.value.toLocaleString("ro-RO"), change: data.stats.total_users.change_pct, color: "blue" },
      { label: "Cluburi active", value: data.stats.active_clubs.value.toLocaleString("ro-RO"), change: data.stats.active_clubs.change_pct, color: "emerald" },
      { label: "Sesiuni luna curentă", value: data.stats.sessions_this_month.value.toLocaleString("ro-RO"), change: data.stats.sessions_this_month.change_pct, color: "purple" },
      { label: "Quiz-uri finalizate", value: data.stats.quiz_completions.value.toLocaleString("ro-RO"), change: data.stats.quiz_completions.change_pct, color: "amber" },
      { label: "Puncte acordate", value: data.stats.total_points_awarded.value.toLocaleString("ro-RO"), change: data.stats.total_points_awarded.change_pct, color: "rose" },
      {
        label: "Venit (RON)",
        value: (data.stats.revenue_ron_cents.value / 100).toLocaleString("ro-RO", { maximumFractionDigits: 0 }),
        change: data.stats.revenue_ron_cents.change_pct,
        color: "cyan",
      },
    ];
  }, [data]);

  const userGrowthChart = useMemo(() => {
    if (!data) return null;
    const labels = data.monthly_growth.map((m) => {
      const [y, mo] = m.month.split("-");
      return `${MONTH_LABELS_RO[parseInt(mo, 10) - 1] || mo} ${y.slice(2)}`;
    });
    return {
      labels,
      datasets: [
        {
          label: "Utilizatori noi",
          data: data.monthly_growth.map((m) => m.new_users),
          borderColor: "rgb(59, 130, 246)",
          backgroundColor: "rgba(59, 130, 246, 0.1)",
          fill: true,
          tension: 0.4,
        },
        {
          label: "Utilizatori activi",
          data: data.monthly_growth.map((m) => m.active_users),
          borderColor: "rgb(16, 185, 129)",
          backgroundColor: "rgba(16, 185, 129, 0.1)",
          fill: true,
          tension: 0.4,
        },
      ],
    };
  }, [data]);

  const weekdayChart = useMemo(() => {
    if (!data) return null;
    return {
      labels: data.weekday_activity.map((d) => WEEKDAY_LABELS_RO[d.weekday]),
      datasets: [
        {
          label: "Sesiuni",
          data: data.weekday_activity.map((d) => d.sessions),
          backgroundColor: "rgba(139, 92, 246, 0.8)",
          borderColor: "rgb(139, 92, 246)",
          borderWidth: 1,
          borderRadius: 6,
        },
        {
          label: "Prezență",
          data: data.weekday_activity.map((d) => d.attendance),
          backgroundColor: "rgba(236, 72, 153, 0.8)",
          borderColor: "rgb(236, 72, 153)",
          borderWidth: 1,
          borderRadius: 6,
        },
      ],
    };
  }, [data]);

  const revenueChart = useMemo(() => {
    if (!data || data.revenue_by_status.length === 0) return null;
    const positive = data.revenue_by_status.filter((r) => (r.ron_cents || r.eur_cents) > 0);
    if (positive.length === 0) return null;
    return {
      labels: positive.map((r) => STATUS_LABEL[r.status] || r.status),
      datasets: [
        {
          data: positive.map((r) => (r.ron_cents || r.eur_cents) / 100),
          backgroundColor: [
            "rgba(251, 191, 36, 0.85)",
            "rgba(148, 163, 184, 0.85)",
            "rgba(180, 83, 9, 0.85)",
            "rgba(59, 130, 246, 0.85)",
            "rgba(16, 185, 129, 0.85)",
            "rgba(239, 68, 68, 0.85)",
          ],
          borderWidth: 2,
        },
      ],
    };
  }, [data]);

  const pointsChart = useMemo(() => {
    if (!data || data.points_by_source.length === 0) return null;
    const positive = data.points_by_source.filter((r) => r.points > 0);
    if (positive.length === 0) return null;
    return {
      labels: positive.map((r) => SOURCE_LABEL[r.source] || r.source),
      datasets: [
        {
          data: positive.map((r) => r.points),
          backgroundColor: [
            "rgba(139, 92, 246, 0.85)",
            "rgba(16, 185, 129, 0.85)",
            "rgba(59, 130, 246, 0.85)",
            "rgba(245, 158, 11, 0.85)",
            "rgba(236, 72, 153, 0.85)",
            "rgba(239, 68, 68, 0.85)",
            "rgba(20, 184, 166, 0.85)",
          ],
          borderWidth: 2,
        },
      ],
    };
  }, [data]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { labels: { color: "#475569", font: { size: 12 } } },
      tooltip: {
        backgroundColor: "rgba(255, 255, 255, 0.95)",
        titleColor: "#1e293b",
        bodyColor: "#475569",
        borderColor: "#e2e8f0",
        borderWidth: 1,
      },
    },
    scales: {
      x: { ticks: { color: "#64748b" }, grid: { color: "rgba(226, 232, 240, 0.5)" } },
      y: { ticks: { color: "#64748b" }, grid: { color: "rgba(226, 232, 240, 0.5)" } },
    },
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "bottom" as const, labels: { color: "#475569", font: { size: 11 }, padding: 15 } },
      tooltip: {
        backgroundColor: "rgba(255, 255, 255, 0.95)",
        titleColor: "#1e293b",
        bodyColor: "#475569",
        borderColor: "#e2e8f0",
        borderWidth: 1,
      },
    },
  };

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="mx-auto max-w-[1500px]">
        {/* Header */}
        <AdminPageHeader
          eyebrow="Platformă"
          title="Rapoarte și Analize"
          subtitle="Statistici platforma — toate datele provin din DB"
          icon={
            <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          }
        />

        <div className="flex gap-1 p-1 mb-6 border rounded-xl border-slate-200 bg-slate-100 w-fit">
          {([["analytics", "Analize platformă"], ["attendance", "Raport de prezență"]] as const).map(
            ([id, label]) => (
              <button
                key={id}
                onClick={() => setTab(id)}
                className={`px-4 py-2 text-sm font-semibold rounded-lg transition-colors ${
                  tab === id ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"
                }`}
              >
                {label}
              </button>
            )
          )}
        </div>

        {tab === "attendance" && (
          <div className="mb-6">
            <AttendanceReportPanel />
          </div>
        )}

        {tab === "analytics" && (
        <>
        {/* Date Range */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 mb-6">
          <div className="flex flex-col md:flex-row items-start md:items-end justify-between gap-4">
            <div className="flex flex-wrap items-end gap-4">
              <div>
                <label className="block text-xs text-slate-500 mb-2 font-medium">De la</label>
                <DateInput
                  value={dateRange.start}
                  onChange={(iso) => setDateRange({ ...dateRange, start: iso })}
                  className="px-4 py-2.5 bg-white border border-slate-300 rounded-xl text-slate-700 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-2 font-medium">Până la</label>
                <DateInput
                  value={dateRange.end}
                  onChange={(iso) => setDateRange({ ...dateRange, end: iso })}
                  className="px-4 py-2.5 bg-white border border-slate-300 rounded-xl text-slate-700 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
              </div>
              <button
                onClick={onApplyRange}
                disabled={loading}
                className="px-4 py-2.5 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white rounded-xl text-sm font-medium"
              >
                Aplică
              </button>
            </div>
            {data?.period && (
              <p className="text-xs text-slate-500">
                Perioadă: {formatDate(data.period.start)} – {formatDate(data.period.end)}
              </p>
            )}
          </div>
        </div>

        {loading ? (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-20 text-center">
            <div className="inline-flex items-center gap-3">
              <div className="w-6 h-6 border-2 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
              <span className="text-slate-600">Se încarcă...</span>
            </div>
          </div>
        ) : !data ? (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-20 text-center text-slate-500">
            Nu există date disponibile.
          </div>
        ) : (
          <>
            {/* Statistics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
              {stats.map((stat) => (
                <div key={stat.label} className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className={`p-3 bg-${stat.color}-50 rounded-xl`}>
                      <span className={`text-${stat.color}-600 text-sm font-medium`}>{stat.label.split(" ")[0]}</span>
                    </div>
                    {stat.change !== null && (
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          stat.change >= 0
                            ? "bg-emerald-50 text-emerald-600"
                            : "bg-red-50 text-red-500"
                        }`}
                      >
                        {stat.change > 0 ? "+" : ""}
                        {stat.change}%
                      </span>
                    )}
                  </div>
                  <h3 className="text-3xl font-bold text-slate-800 mb-1">{stat.value}</h3>
                  <p className="text-sm text-slate-500 font-medium">{stat.label}</p>
                </div>
              ))}
            </div>

            {/* Charts row 1 */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
                <h3 className="text-lg font-semibold text-slate-800 mb-4">Creștere utilizatori (12 luni)</h3>
                <div className="h-80">
                  {userGrowthChart ? (
                    <Line data={userGrowthChart} options={chartOptions} />
                  ) : (
                    <p className="text-slate-400 text-center mt-32">Date insuficiente</p>
                  )}
                </div>
              </div>
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
                <h3 className="text-lg font-semibold text-slate-800 mb-4">Activitate sesiuni (7 zile)</h3>
                <div className="h-80">
                  {weekdayChart ? (
                    <Bar data={weekdayChart} options={chartOptions} />
                  ) : (
                    <p className="text-slate-400 text-center mt-32">Date insuficiente</p>
                  )}
                </div>
              </div>
            </div>

            {/* Charts row 2 */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
                <h3 className="text-lg font-semibold text-slate-800 mb-4">Venit pe status factură</h3>
                <div className="h-80 flex items-center justify-center">
                  {revenueChart ? (
                    <Doughnut data={revenueChart} options={doughnutOptions} />
                  ) : (
                    <p className="text-slate-400">Nicio factură înregistrată</p>
                  )}
                </div>
              </div>
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
                <h3 className="text-lg font-semibold text-slate-800 mb-4">Distribuție puncte</h3>
                <div className="h-80 flex items-center justify-center">
                  {pointsChart ? (
                    <Doughnut data={pointsChart} options={doughnutOptions} />
                  ) : (
                    <p className="text-slate-400">Niciun punct înregistrat încă</p>
                  )}
                </div>
              </div>
            </div>

            {/* Top performers */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
                <h3 className="text-lg font-semibold text-slate-800 mb-4">Top cluburi</h3>
                {data.top_clubs.length === 0 ? (
                  <p className="text-slate-400">Niciun club cu sportivi atașați.</p>
                ) : (
                  <div className="space-y-3">
                    {data.top_clubs.map((club, idx) => (
                      <div key={club.id} className="p-4 bg-slate-50 rounded-xl border border-slate-100">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="font-semibold text-slate-800">{club.name}</h4>
                          <span className="px-2.5 py-1 bg-amber-50 text-amber-600 text-xs rounded-full font-bold">
                            #{idx + 1}
                          </span>
                        </div>
                        <div className="grid grid-cols-3 gap-3 text-sm">
                          <div className="bg-white rounded-lg p-2 border border-slate-100">
                            <p className="text-slate-400 text-xs mb-1">Sportivi</p>
                            <p className="text-slate-800 font-bold">{club.athletes}</p>
                          </div>
                          <div className="bg-white rounded-lg p-2 border border-slate-100">
                            <p className="text-slate-400 text-xs mb-1">Sesiuni</p>
                            <p className="text-slate-800 font-bold">{club.sessions}</p>
                          </div>
                          <div className="bg-white rounded-lg p-2 border border-slate-100">
                            <p className="text-slate-400 text-xs mb-1">Venit (RON)</p>
                            <p className="text-emerald-600 font-bold">
                              {(club.revenue_ron_cents / 100).toLocaleString("ro-RO", { maximumFractionDigits: 0 })}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
                <h3 className="text-lg font-semibold text-slate-800 mb-4">Top sportivi după puncte</h3>
                {data.top_athletes.length === 0 ? (
                  <p className="text-slate-400">Niciun sportiv cu puncte înregistrate.</p>
                ) : (
                  <div className="space-y-3">
                    {data.top_athletes.map((athlete) => (
                      <div key={athlete.id} className="p-4 bg-slate-50 rounded-xl border border-slate-100">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="font-semibold text-slate-800">{athlete.name}</h4>
                          <span className="px-2.5 py-1 bg-purple-50 text-purple-600 text-xs rounded-full font-bold">
                            {athlete.points} pts
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div className="bg-white rounded-lg p-2 border border-slate-100">
                            <p className="text-slate-400 text-xs mb-1">Quiz-uri</p>
                            <p className="text-slate-800 font-bold">{athlete.quizzes}</p>
                          </div>
                          <div className="bg-white rounded-lg p-2 border border-slate-100">
                            <p className="text-slate-400 text-xs mb-1">Prezență</p>
                            <p className="text-emerald-600 font-bold">{athlete.attendance_rate}%</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
        </>
        )}
      </div>
    </div>
  );
}
